import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLiveAnnouncer } from '@/shared/hooks/useLiveAnnouncer/useLiveAnnouncer';

import { type SelectionChangeOutcome } from '../useSelectionValue/useSelectionValue';

export interface UseSelectionAnnouncer {
  /** Rendered into the panel's one `role="status"` region. Empty while idle. */
  announcement: string;
  say: (text: string) => void;
  /** Maps a change attempt to what the operator should hear about it. */
  announceOutcome: (outcome: SelectionChangeOutcome) => void;
  /** The refusal a read-only field owes: silence here reads as a broken control. */
  announceReadOnlyRefusal: () => void;
}

/** The `uiKit` sentence a picker owes when it refuses an edit. */
const READ_ONLY_REFUSAL_KEY = 'multiSelect.readOnlyRefused';

/**
 * What the selection family says out loud.
 *
 * One hook rather than a copy per control, because the announcements ARE the
 * behaviour: a ceiling that refuses in silence, or a read-only row that clicks
 * and does nothing, is a worse failure than the `disabled` treatment either one
 * replaced. `ServerSelect` held its own copy of the read-only line until this
 * existed — the fifth drift surface in a family whose whole point is not having
 * any.
 *
 * The state and the refusal moved to the shared `useLiveAnnouncer` in P10.2c;
 * what stays here is the one thing no other family has — the ceiling outcome,
 * which only selection can produce.
 */
export function useSelectionAnnouncer(): UseSelectionAnnouncer {
  const { t } = useTranslation('uiKit');
  const { announcement, say, clear, announceReadOnlyRefusal } =
    useLiveAnnouncer(READ_ONLY_REFUSAL_KEY);

  const announceOutcome = useCallback(
    (outcome: SelectionChangeOutcome) => {
      if (outcome.status === 'refused') {
        say(t('multiSelect.limitBlocked', { max: outcome.max }));
      } else if (outcome.status === 'partial') {
        say(t('multiSelect.limitReached', { max: outcome.max }));
      } else {
        // A change that went through says nothing: the row's own checked state
        // already announced it, and repeating it would talk over the reader.
        clear();
      }
    },
    [clear, say, t],
  );

  // Memoized: a fresh object every render makes every dependent `useCallback`
  // decorative, which is worse than not having one — it reads as memoized and
  // is not.
  return useMemo(
    () => ({ announcement, say, announceOutcome, announceReadOnlyRefusal }),
    [announcement, announceOutcome, announceReadOnlyRefusal, say],
  );
}
